/* eslint-disable no-unused-vars */
import { CircularProgress, Tab, Tabs } from "@mui/material";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import DatePicker from "../../../components/common/DatePicker";
import DropDownAlternate from "../../../components/common/dropDownAlternate";
// components
import Pagination from "../../../components/common/Pagination";
import SearchInput from "../../../components/common/searchInput";
import ContractsListItem from "../../../components/contractListItem";
import {
  ACTIVE_STATUS,
  CLOSED_STATUS,
  COMPLETED_STATUS,
  ESCALATION_STATUS,
  ITEM_ACCEPTED_STATUS,
  RESOLVED_STATUS,
  WAITING_FOR_INSPECTION,
  WAITING_FOR_SHIPPING,
} from "../../../components/contractListItem/constants";
import { getContracts } from "../../../setup/api/services/contract";
import { IContractDetailsResponseData } from "../../../setup/api/services/contract/models";
import { AuthConfig } from "../../../setup/context/config/authConfig";
import { BUTTON } from "./constants";
import { EmptyContractsList } from "./emptyContractList";
import styles from "./list.module.scss";

const statuses = [{ id: "ALL_CASES", name: "جميع الحالات" }];

const active_status = [
  { id: "ALL_CASES", name: "جميع الحالات" },
  { id: "CREATED", name: ACTIVE_STATUS },
  { id: "WAITING_FOR_DELIVERY", name: WAITING_FOR_SHIPPING },
  { id: "ITEM_INSPECTION", name: WAITING_FOR_INSPECTION },
  { id: "ITEM_ACCEPTED", name: ITEM_ACCEPTED_STATUS },
  { id: "DELIVERY_ESCALATION,INSPECTION_ESCALATION", name: ESCALATION_STATUS },
  {
    id: "RESOLVED_REFUND_CUSTOMER,RESOLVED_RELEASE_PROVIDER",
    name: RESOLVED_STATUS,
  },
];

const closed_status = [
  { id: "ALL_CASES", name: "جميع الحالات" },
  { id: "COMPLETED", name: COMPLETED_STATUS },
  { id: "CLOSED_REFUND_CUSTOMER,CLOSED_RELEASE_PROVIDER", name: CLOSED_STATUS },
];

interface Props {
  userId?: string;
}

const ContractsList = ({ userId }: Props) => {
  const [tabType, setTabType] = React.useState("active");
  const [contracts, setContracts] = React.useState<
    IContractDetailsResponseData[]
  >([]);
  const [page, setPage] = React.useState(1);
  const [pagesCount, setPagesCount] = React.useState(1);
  const [activeTabPage, setActiveTabPage] = React.useState(1);
  const [prevTabPage, setPrevTabPage] = React.useState(1);
  const pageSize = 10;
  const [searchValue, setSearchValue] = React.useState<string | number>("");

  const user_id = userId || AuthConfig.getUserInfo().id;
  const [status, setStatus] = useState<{ id: string | number; name: string }>({
    id: "",
    name: "",
  });

  const [createdAtStart, setCreatedAtStart] = React.useState<any>(null);
  const [createdAtEnd, setCreatedAtEnd] = React.useState<any>(null);

  const [loading, setLoading] = useState<boolean>(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabType(newValue);
    setStatus(statuses[0]);
    if (newValue === "previous") {
      setPage(prevTabPage != 1 ? prevTabPage : 1);
    } else {
      setPage(activeTabPage != 1 ? activeTabPage : 1);
    }
  };

  const handlePaginationChange = (value: number) => {
    setPage(value);
    if (tabType === "active") {
      setActiveTabPage(value);
    } else {
      setPrevTabPage(value);
    }
  };

  const fetchContracts = useCallback(
    async (listType, status, contractId, createdAtTo, createdAtFrom) => {
      setLoading(true);
      try {
        const body = {
          listType: listType,
          status: status,
          contractId: contractId,
          createdAtTo: createdAtTo
            ? moment(createdAtTo).endOf("day").format("YYYY-MM-DD HH:mm")
            : "",
          createdAtFrom: createdAtFrom
            ? moment(createdAtFrom).format("YYYY-MM-DD HH:mm")
            : "",
          page: page - 1,
          size: pageSize,
        };
        const response = await getContracts(body);
        setContracts(response.data.content);
        setPagesCount(response.data.totalPages);
        setLoading(false);
      } catch (error) {
        Sentry.captureException(error);
        setLoading(false);
      }
    },
    [page]
  );

  useEffect(() => {
    fetchContracts(
      tabType,
      status?.id,
      searchValue,
      createdAtEnd,
      createdAtStart
    );
  }, [fetchContracts, searchValue, status?.id, tabType]);

  const search = useCallback(() => {
    fetchContracts(
      tabType,
      status?.id,
      searchValue,
      createdAtEnd
        ? createdAtEnd
        : moment(new Date()).endOf("day").format("YYYY-MM-DD HH:mm"),
      createdAtStart
        ? createdAtStart
        : moment(new Date()).startOf("day").format("YYYY-MM-DD HH:mm")
    );
  }, [
    createdAtEnd,
    createdAtStart,
    fetchContracts,
    searchValue,
    status,
    tabType,
  ]);

  const searchViaStartDate = useCallback(
    (startDate: Date) => {
      fetchContracts(
        tabType,
        status?.id,
        searchValue,
        createdAtEnd
          ? createdAtEnd
          : moment(new Date()).endOf("day").format("YYYY-MM-DD HH:mm"),
        startDate
      );
    },
    [createdAtEnd, fetchContracts, searchValue, status, tabType]
  );

  const searchViaEndDate = useCallback(
    (endDate: Date) => {
      fetchContracts(
        tabType,
        status?.id,
        searchValue,
        endDate,
        createdAtStart
          ? createdAtStart
          : moment(new Date()).startOf("day").format("YYYY-MM-DD HH:mm")
      );
    },
    [createdAtStart, fetchContracts, searchValue, status?.id, tabType]
  );

  const fetchAllContract = () => {
    fetchContracts(
      tabType,
      status?.id,
      searchValue,
      createdAtEnd,
      createdAtStart
    );
  };

  const { t } = useTranslation();

  return (
    <div className={styles.container}>
      <div className={"page_layout"}>
        <div className={styles["page_header"]}>
          <h1>{t("lbl_header_contract")}</h1>
          <p className={classNames("subtitle1", "g700")}>
            {t("lbl_brief_contract")}
          </p>
        </div>

        <div>
          <div className={styles.tabsRow}>
            <div className={styles.tab_container}>
              <Tabs
                style={{ alignItems: "center" }}
                className={styles.tab_wrapper}
                value={tabType}
                onChange={handleTabChange}>
                <Tab
                  value="active"
                  label={t("lbl_home_active_transactions_title")}
                  className={tabType === "active" ? "tab_h3" : "tab_p1"}
                />
                <div style={{ width: "21px" }} />
                <Tab
                  value="previous"
                  label={t("lbl_previous_contracts_tab")}
                  className={tabType === "previous" ? "tab_h3" : "tab_p1"}
                />
              </Tabs>
              <div
                className={classNames(
                  styles.filter_wrapper,
                  styles.filter_search_bar_tablet
                )}>
                <SearchInput
                  dir="rtl"
                  placeholder={`${BUTTON.SEARCH}...`}
                  value={searchValue}
                  handleSearchClick={search}
                  handleChange={(value) => {
                    setSearchValue(value);
                  }}
                />
              </div>
            </div>
            <div className={styles.thirdTab}>
              <div className={styles.filter_drop_down_mobile}>
                <DropDownAlternate
                  data={tabType === "active" ? active_status : closed_status}
                  label={BUTTON.ALL_CASES}
                  shownValue={status?.id === "" ? statuses[0] : status}
                  handleChange={(value) => setStatus(value)}
                />
              </div>
              <div
                className={classNames(
                  styles.filter_wrapper,
                  styles.filter_search_bar_mobile
                )}>
                <SearchInput
                  dir="rtl"
                  placeholder={`${BUTTON.SEARCH}...`}
                  value={searchValue}
                  handleSearchClick={search}
                  handleChange={(value) => {
                    setSearchValue(value);
                  }}
                />
              </div>
            </div>

            <div className={styles.filter_container}>
              <div className={styles.filter_drop_down}>
                <DropDownAlternate
                  data={tabType === "active" ? active_status : closed_status}
                  label={BUTTON.ALL_CASES}
                  shownValue={status?.id === "" ? statuses[0] : status}
                  handleChange={(value) => setStatus(value)}
                />
              </div>
              <div
                className={classNames(
                  styles.filter_wrapper,
                  styles.filter_date_picker
                )}>
                <DatePicker
                  value={createdAtStart}
                  placeholder={BUTTON.START_DATE}
                  handleChange={(date) => {
                    searchViaStartDate(date);
                    if (createdAtEnd === null) {
                      setCreatedAtEnd(
                        moment(new Date())
                          .endOf("day")
                          .format("YYYY-MM-DD HH:mm")
                      );
                    }
                    setCreatedAtStart(date);
                  }}
                  maxDate={new Date()}
                />
              </div>
              <div
                className={classNames(
                  styles.filter_wrapper,
                  styles.filter_date_picker
                )}>
                <DatePicker
                  value={createdAtEnd}
                  placeholder={BUTTON.END_DATE}
                  disabled={createdAtStart === null}
                  handleChange={(date) => {
                    searchViaEndDate(date);
                    setCreatedAtEnd(date);
                  }}
                  maxDate={new Date()}
                  minDate={createdAtStart}
                />
              </div>
              <div
                style={{ width: "12rem" }}
                className={classNames(
                  styles.filter_wrapper,
                  styles.filter_search_bar
                )}>
                <SearchInput
                  dir="rtl"
                  placeholder={`${BUTTON.SEARCH}...`}
                  handleSearchClick={search}
                  value={searchValue}
                  handleChange={(value) => {
                    setSearchValue(value);
                  }}
                />
              </div>
            </div>
          </div>
          <div className={classNames(styles.listSection)}>
            {loading && (
              <div className={classNames(styles.spinner)}>
                <CircularProgress />
              </div>
            )}

            {!loading && contracts.length ? (
              contracts.map((contract: any) => {
                return (
                  <div className={styles.list_wrapper} key={contract.id}>
                    <ContractsListItem
                      userId={user_id}
                      contract={contract}></ContractsListItem>
                  </div>
                );
              })
            ) : (
              <div
                className={classNames(styles.emptyListContainer)}
                style={{ display: loading ? "none" : "flex" }}>
                <EmptyContractsList
                  pageType={tabType}
                  feedBack={fetchAllContract}
                />
              </div>
            )}
          </div>
        </div>
        <div
          className={
            contracts.length ? styles.paginatorContainer : styles.hide
          }>
          <Pagination
            pagesCount={pagesCount}
            page={page}
            length={pageSize}
            handlePaginationChange={handlePaginationChange}
          />
        </div>
      </div>
    </div>
  );
};

export default ContractsList;
